"use client";

import { useEffect } from "react";
import type { EvaluationDetails } from "@statsig/js-client";
import * as Sentry from "@sentry/nextjs";

/**
 * Hook that logs Statsig experiment evaluations to Sentry.
 * Logs breadcrumbs for all evaluations and captures exceptions for unhealthy states.
 */
export function useExperimentLogger(
  experimentName: string,
  key: string,
  value: string,
  evaluationDetails: EvaluationDetails
): void {
  useEffect(() => {
    const reason = evaluationDetails.reason || "";
    const isUnhealthy =
      reason.includes("NoValues") ||
      reason.includes("Unrecognized") ||
      reason.includes("Error");

    Sentry.addBreadcrumb({
      message: "Statsig experiment evaluated",
      category: "statsig",
      level: isUnhealthy ? "warning" : "info",
      data: {
        experimentName,
        key,
        value,
        reason,
        lcut: evaluationDetails.lcut,
        receivedAt: evaluationDetails.receivedAt,
        timestamp: new Date().toISOString(),
      },
    });

    if (isUnhealthy) {
      Sentry.captureException(
        new Error(`Statsig unhealthy evaluation: ${reason}`),
        {
          tags: {
            component: "statsig",
            evaluationReason: reason,
          },
          extra: {
            experimentName,
            key,
            value,
            lcut: evaluationDetails.lcut,
            receivedAt: evaluationDetails.receivedAt,
          },
        }
      );
    }
  }, [experimentName, key, value, evaluationDetails]);
}

export default useExperimentLogger;
